import { Box, ButtonBase, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FunnelReport } from '../services/responses/dashboard.response';
import { Panel, PanelStat, PanelState, percent, useDashboardColors } from './panel';

interface Props {
    data?: FunnelReport;
    selectedColumn: string;
    onPickColumn: (id: string) => void;
}

/// Quantos leads do período chegaram a cada etapa, na ordem do quadro; a maior queda é destacada.
export const FunnelColumns = ({ data, selectedColumn, onPickColumn }: Props) => {
    const { t } = useTranslation();
    const cc = useDashboardColors();

    const stages = data?.stages ?? [];
    // Proporcional à maior etapa, não à entrada: quem pula uma etapa adiante pode superar a primeira.
    const largest = Math.max(1, ...stages.map((stage) => stage.reached));

    const worst = stages
        .filter((stage) => (stage.dropRate ?? 0) > 0)
        .sort((a, b) => (b.dropRate ?? 0) - (a.dropRate ?? 0))[0];

    return (
        <Panel
            title={t('painel.funilTitulo')}
            subtitle={t('painel.funilAjuda')}
            stats={data && data.entered > 0 && (
                <>
                    <PanelStat label={t('painel.entraram')} value={data.entered} />
                    {worst && (
                        <PanelStat
                            label={t('painel.maiorQueda')}
                            value={`${worst.name} · ${percent(worst.dropRate ?? 0)}`}
                            alert
                        />
                    )}
                </>
            )}
            footer={stages.length > 0 && data?.entered ? t('painel.funilRodape') : undefined}
        >
            {!data || stages.length === 0 || data.entered === 0 ? (
                <PanelState loading={!data} empty={t('crmDashboard.semDados')} height={220} />
            ) : (
                <Box sx={{ overflowX: 'auto' }}>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: `repeat(${stages.length}, minmax(76px, 1fr))`,
                            gap: '2px',
                            alignItems: 'end',
                            minWidth: stages.length * 78,
                        }}
                    >
                        {stages.map((stage) => {
                            const isWorst = worst?.columnId === stage.columnId;
                            const selected = selectedColumn === stage.columnId;

                            return (
                                <Tooltip
                                    key={stage.columnId}
                                    arrow
                                    title={
                                        t('crmDashboard.quedaDetalhe', { alcancou: stage.reached, agora: stage.current })
                                        + (stage.droppedToNext
                                            ? ` · ${t('crmDashboard.perdeuAqui', {
                                                total: stage.droppedToNext,
                                                taxa: stage.dropRate?.toLocaleString('pt-BR') ?? '—',
                                            })}`
                                            : '')
                                    }
                                >
                                    <ButtonBase
                                        // O filtro é por etapa atual: sem lead parado nela, o painel ficaria vazio. Selecionada continua clicável para desmarcar.
                                        onClick={() => (stage.current > 0 || selected) && onPickColumn(stage.columnId)}
                                        aria-pressed={selected}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'stretch',
                                            gap: 0.6,
                                            px: 0.5,
                                            pt: 0.5,
                                            borderRadius: '6px',
                                            background: selected ? cc.accentSoftBg : 'transparent',
                                            cursor: stage.current > 0 || selected ? 'pointer' : 'default',
                                            '&:hover': { background: stage.current > 0 || selected ? cc.countBg : 'transparent' },
                                        }}
                                    >
                                        <Typography
                                            sx={{ color: cc.textPrimary, fontWeight: 700, fontSize: '0.8rem', textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}
                                        >
                                            {stage.reached}
                                        </Typography>
                                        <Box sx={{ height: 150, display: 'flex', alignItems: 'flex-end' }}>
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    height: `${Math.max(2, (stage.reached / largest) * 100)}%`,
                                                    borderRadius: '4px 4px 0 0',
                                                    background: isWorst ? cc.eventDanger : cc.accent,
                                                    transition: 'height .25s',
                                                }}
                                            />
                                        </Box>
                                        <Box sx={{ textAlign: 'center', pb: 0.5, borderTop: `1px solid ${cc.dividerStrong}`, pt: 0.6 }}>
                                            <Typography
                                                sx={{
                                                    color: cc.textPrimary,
                                                    fontWeight: 600,
                                                    fontSize: '0.72rem',
                                                    lineHeight: 1.2,
                                                    // Sempre duas linhas de espaço: a base das barras fica alinhada com nome curto ou longo.
                                                    height: '2.4em',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                {stage.name}
                                            </Typography>
                                            {stage.shareOfEntry != null && (
                                                <Typography sx={{ color: cc.textMuted, fontSize: '0.66rem' }}>
                                                    {t('painel.daEntrada', { taxa: percent(stage.shareOfEntry) })}
                                                </Typography>
                                            )}
                                        </Box>
                                    </ButtonBase>
                                </Tooltip>
                            );
                        })}
                    </Box>
                </Box>
            )}
        </Panel>
    );
};
